/**
 * @fileoverview Data Layer (DL) for the Buzzer Battle game dynamic ("De barrio en barrio").
 * Provides native fetch HTTP requests to communicate with the BuzzerBattle API controller endpoints.
 * 
 * @module utilities/DL/BuzzerBattle
 */

// =============================================================================
// THEMES CRUD ENDPOINTS
// =============================================================================

/**
 * Retrieves all registered themes for Buzzer Battle.
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetThemes = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetThemes`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener la lista de temas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener temas.' };
    }
};

/**
 * Retrieves a single Buzzer Battle theme by its unique numeric identifier.
 * 
 * @async
 * @function GetTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetTheme/${themeIdentifier}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el tema seleccionado.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener el tema.' };
    }
};

/**
 * Creates or updates a Buzzer Battle theme configuration.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} themeData - Theme payload object.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveTheme = async (themeData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/SaveTheme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al guardar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al guardar el tema.' };
    }
};

/**
 * Deletes a theme by its unique numeric identifier.
 * 
 * @async
 * @function DeleteTheme
 * @param {number} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, message?: string}>} API response payload.
 */
export const DeleteTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/DeleteTheme/${themeIdentifier}`, {
            method: 'DELETE'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al eliminar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al eliminar el tema.' };
    }
};

/**
 * Clones an existing theme by ID into a new theme record.
 * 
 * @async
 * @function CloneTheme
 * @param {number} themeIdentifier - Source theme ID to clone.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const CloneTheme = async (themeIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/CloneTheme/${themeIdentifier}`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al clonar el tema.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al clonar el tema.' };
    }
};

// =============================================================================
// QUESTION SETS CRUD ENDPOINTS
// =============================================================================

/**
 * Retrieves all registered question sets for Buzzer Battle.
 * 
 * @async
 * @function GetQuestionSets
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetQuestionSets = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetQuestionSets`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener los bancos de preguntas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener bancos de preguntas.' };
    }
};

/**
 * Retrieves a single question set with questions and options by its unique identifier.
 * 
 * @async
 * @function GetQuestionSet
 * @param {number} questionSetIdentifier - Unique question set ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetQuestionSet = async (questionSetIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetQuestionSet/${questionSetIdentifier}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el banco de preguntas seleccionado.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener el banco de preguntas.' };
    }
};

/**
 * Creates or updates a question set including nested questions and options.
 * 
 * @async
 * @function SaveQuestionSet
 * @param {Object} questionSetData - Question set payload object.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveQuestionSet = async (questionSetData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/SaveQuestionSet`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(questionSetData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al guardar el banco de preguntas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al guardar el banco de preguntas.' };
    }
};

/**
 * Deletes a question set by ID (cascade deletes questions and options).
 * 
 * @async
 * @function DeleteQuestionSet
 * @param {number} questionSetIdentifier - Unique question set ID.
 * @returns {Promise<{success: boolean, message?: string}>} API response payload.
 */
export const DeleteQuestionSet = async (questionSetIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/DeleteQuestionSet/${questionSetIdentifier}`, {
            method: 'DELETE'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al eliminar el banco de preguntas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al eliminar el banco de preguntas.' };
    }
};

/**
 * Clones an existing question set by ID with all questions and options into a new record.
 * 
 * @async
 * @function CloneQuestionSet
 * @param {number} questionSetIdentifier - Source question set ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const CloneQuestionSet = async (questionSetIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/CloneQuestionSet/${questionSetIdentifier}`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al clonar el banco de preguntas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al clonar el banco de preguntas.' };
    }
};

// =============================================================================
// MATCHES HISTORY & MEDIA UPLOAD ENDPOINTS
// =============================================================================

/**
 * Retrieves the historical log of completed Buzzer Battle matches.
 * 
 * @async
 * @function GetMatchesHistory
 * @param {number} [queryLimit=50] - Maximum number of matches to retrieve.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetMatchesHistory = async (queryLimit = 50) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetMatchesHistory?limit=${queryLimit}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el historial de partidas.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener historial de partidas.' };
    }
};

/**
 * Records the final outcome of a Buzzer Battle match in database.
 * 
 * @async
 * @function SaveMatch
 * @param {Object} matchData - Match result payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveMatch = async (matchData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/SaveMatch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(matchData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al registrar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al registrar la partida.' };
    }
};

/**
 * Uploads a single media file to be compressed and saved on the server.
 * 
 * @async
 * @function UploadMedia
 * @param {FormData} multipartFormData - Multipart form data containing 'file' and optional 'type'.
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} API response payload.
 */
export const UploadMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/UploadMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir el archivo multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir archivo multimedia.' };
    }
};

/**
 * Uploads a batch of media files to be compressed and saved simultaneously on the server.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {FormData} multipartFormData - Multipart form data containing multiple 'files' and optional 'type'.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} API response payload.
 */
export const UploadBatchMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/UploadBatchMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir lote de archivos multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir lote multimedia.' };
    }
};

// =============================================================================
// REALTIME ENGINE BACKUP ENDPOINTS (REST ALTERNATIVE TO WEBSOCKETS)
// =============================================================================

/**
 * Emits a real-time command to project the cover promotional screen.
 * 
 * @async
 * @function ShowCover
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ShowCover = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ShowCover`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al mostrar la portada.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al mostrar la portada.' };
    }
};

/**
 * Emits a real-time command to project the game arena HUD screen.
 * 
 * @async
 * @function ShowGame
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ShowGame = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ShowGame`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al proyectar el escenario de juego.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al proyectar el juego.' };
    }
};

/**
 * Initializes a new match with theme, question set, and optional custom player configuration.
 * 
 * @async
 * @function InitMatch
 * @param {Object} initMatchParameters - Parameters object containing themeId, questionSetId, and optional customPlayers.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const InitMatch = async (initMatchParameters) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/InitMatch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(initMatchParameters)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al inicializar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al inicializar la partida.' };
    }
};

/**
 * Launches an active question or a specific question by index to stadium screen and fan mobiles.
 * 
 * @async
 * @function LaunchQuestion
 * @param {number|null} [questionIndex=null] - Optional zero-based question index.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const LaunchQuestion = async (questionIndex = null) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/LaunchQuestion`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(questionIndex !== null && questionIndex !== undefined ? { questionIndex } : {})
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al lanzar la pregunta.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al lanzar la pregunta.' };
    }
};

/**
 * Displays response options on screen and mobile pulsers.
 * 
 * @async
 * @function ShowOptions
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ShowOptions = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ShowOptions`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al mostrar las opciones.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al mostrar opciones.' };
    }
};

/**
 * Assigns answering turn to a specific participant.
 * 
 * @async
 * @function AssignTurn
 * @param {string} playerIdentifier - Player ID / keycode to give turn to.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const AssignTurn = async (playerIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/AssignTurn`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ playerId: playerIdentifier })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al asignar el turno.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al asignar el turno.' };
    }
};

/**
 * Records option selected by a player via HTTP REST.
 * 
 * @async
 * @function SelectOption
 * @param {number} optionIdentifier - Selected option ID.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const SelectOption = async (optionIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/SelectOption`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ optionId: optionIdentifier })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al seleccionar la opción.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al seleccionar opción.' };
    }
};

/**
 * Records option selected manually by the operator from the control panel.
 * 
 * @async
 * @function ManualSelectOption
 * @param {number} optionIdentifier - Selected option ID.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ManualSelectOption = async (optionIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ManualSelectOption`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ optionId: optionIdentifier })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al seleccionar manualmente la opción.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al seleccionar manualmente la opción.' };
    }
};

/**
 * Clears the active selected option before verdict reveal.
 * 
 * @async
 * @function ClearSelectedOption
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ClearSelectedOption = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ClearSelectedOption`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al limpiar la selección.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al limpiar la selección.' };
    }
};

/**
 * Reveals whether the chosen answer was correct or incorrect and updates scoreboard.
 * 
 * @async
 * @function RevealResult
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const RevealResult = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/RevealResult`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al revelar el resultado.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al revelar resultado.' };
    }
};

/**
 * Grants the rebound to another player following an incorrect answer or timeout.
 * 
 * @async
 * @function GiveRebound
 * @param {string} nextPlayerIdentifier - Player ID receiving rebound.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const GiveRebound = async (nextPlayerIdentifier) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GiveRebound`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ nextPlayerId: nextPlayerIdentifier })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al otorgar el rebote.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al otorgar el rebote.' };
    }
};

/**
 * Advances to the next question in the set or finishes the match if on last question.
 * 
 * @async
 * @function NextQuestion
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const NextQuestion = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/NextQuestion`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al avanzar a la siguiente pregunta.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al avanzar pregunta.' };
    }
};

/**
 * Rewinds to the previous question (available before starting the match).
 * 
 * @async
 * @function PreviousQuestion
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const PreviousQuestion = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/PreviousQuestion`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al retroceder a la pregunta anterior.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al retroceder pregunta.' };
    }
};

/**
 * Manually adjusts a participant's score (+1 or -1).
 * 
 * @async
 * @function AdjustPlayerScore
 * @param {string} playerId - Identifier of the player (e.g. 'J1', 'J2').
 * @param {number} delta - Delta to adjust (+1 or -1).
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const AdjustPlayerScore = async (playerId, delta) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/AdjustPlayerScore`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ playerId, delta })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al ajustar el puntaje.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al ajustar puntaje.' };
    }
};

/**
 * Projects final podium screen, calculates winner, and persists match in database.
 * 
 * @async
 * @function ShowFinalResults
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ShowFinalResults = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ShowFinalResults`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al proyectar resultados finales.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al proyectar resultados finales.' };
    }
};

/**
 * Resets the active match to idle state.
 * 
 * @async
 * @function ResetMatch
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ResetMatch = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ResetMatch`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al reiniciar la partida.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al reiniciar la partida.' };
    }
};

/**
 * Forces current answer as correct from the admin panel.
 * 
 * @async
 * @function ForceCorrect
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ForceCorrect = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ForceCorrect`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al forzar respuesta correcta.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al forzar respuesta correcta.' };
    }
};

/**
 * Forces current answer as incorrect (strike) from the admin panel.
 * 
 * @async
 * @function ForceIncorrect
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} API response payload.
 */
export const ForceIncorrect = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/ForceIncorrect`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al forzar respuesta incorrecta.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al forzar respuesta incorrecta.' };
    }
};

/**
 * Retrieves the complete synchronized real-time state snapshot.
 * 
 * @async
 * @function GetRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetRealtimeState = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}BuzzerBattle/GetRealtimeState`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el estado en tiempo real.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al consultar el estado en tiempo real.' };
    }
};
